import { createContext, useContext, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik'
import { Userpilot } from 'userpilot'

import ENV_VARS from '@/config'
import { registerLandlord } from '@/models/auth/ports'
import { useDispatch } from '@/lib/redux'
import Yup from '@/lib/yup'
import handleSession from '@/utils/handle-session'
import { getReferralData } from '@/hooks/use-referral-data'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { isValidPlan } from '@/features/Pricing/pricing-plans'
import { EmailErrorIcon } from '@/components/icons'
import { toast } from '@/components/toast-notifications'
import { showWarningModal } from '@/components/warning-modal'

const { DASHBOARD_URL } = ENV_VARS

type AccountContextProps = {
  phoneVerify: boolean
  handlePhoneVerify: (s: boolean) => void
}

type WithChildren = {
  children?: React.ReactNode
}

const Context = createContext<AccountContextProps | undefined>(undefined)

const AccountProvider: React.FC<WithChildren> = ({ children }) => {
  const [phoneVerify, setPhoneVerify] = useState(false)
  const handlePhoneVerify = (newState: boolean) => {
    setPhoneVerify(newState)
  }
  return (
    <Context.Provider value={{ phoneVerify, handlePhoneVerify }}>
      {children}
    </Context.Provider>
  )
}

const useVerify = (): AccountContextProps => {
  const context = useContext(Context)
  if (typeof context === 'undefined') {
    throw new Error('useAccountForm must be used with a AccountFormProvider')
  }
  return context
}

type AccountInfo = {
  first_name: string
  last_name: string
  preferred_name?: string
  password: string
  password_confirm: string
  email: string
  intended_plan?: string
  phone: string
  agree_to_terms?: boolean
  allow_renter_call?: boolean
}

type FormValues = AccountInfo

type Props = {
  children?: React.ReactNode
  accountType?: string
  pageId: string
}

export const useAccountForm = (): FormikProps<FormValues> &
  AccountContextProps => {
  const { phoneVerify, handlePhoneVerify } = useVerify()
  const { isValid, dirty, ...data } = useFormikContext<FormValues>()
  return {
    ...data,
    dirty,
    isValid: isValid && dirty && phoneVerify,
    phoneVerify,
    handlePhoneVerify
  }
}

const AccountForm: React.FC<Props> = ({
  children,
  accountType: plan,
  pageId
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  const accountType = isValidPlan(plan) ? plan : 'essentials'
  const initialValues: FormValues = {
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    email: '',
    preferred_name: '',
    phone: '',
    allow_renter_call: false,
    // Essentials plans will skip checkout, so we can remove this.
    // This means however that upgrades from essentials will need to Contact sales
    intended_plan: accountType !== 'essentials' ? accountType : undefined
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .required(),
    last_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .required(),
    password: Yup.string()
      .min(8)
      .matches(
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?])[A-Za-z\d!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?]+$/,
        "Password doesn't match the requirements."
      )
      .required(),
    password_confirm: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required(),
    email: Yup.string().email().trim().required(),
    intended_plan: Yup.string(),
    phone: Yup.string().phone?.()?.required(),
    agree_to_terms: Yup.boolean()
      .oneOf(
        [true],
        t(
          'b.auth.create.must_accept_conditions.text',
          'Must Accept Terms and Conditions'
        )
      )
      .required()
  })

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const referralData = getReferralData()
    const noCompanyValues = { ...values, company_name: null }
    const { response, body } = await registerLandlord({
      body: {
        ...noCompanyValues,
        code: localStorage.getItem('verified-phone-code'),
        referral_data: referralData
      }
    })

    if (response.ok) {
      dispatch.flags.deleteByKey('intended_plan')
      localStorage.removeItem('verified-phone-code')
      await handleSession(dispatch.session.loadSession())
      window.dataLayer.push({
        event: `landlord-${pageId}-${accountType}-createaccount-submit`,
        userEmail: values.email,
        userFirstName: values.first_name,
        userLastName: values.last_name,
        userPhone: values.phone
      })

      Userpilot.track(`goal_landlord_signup_${accountType}`)

      if (accountType === 'essentials') {
        window.location.href = `${DASHBOARD_URL}/app/profile`
      }

      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          newAccount: true
        }
      })
    } else if (
      body?.message === 'phone number or email already exists in the system'
    ) {
      return showWarningModal({
        title: t('b.auth.create.existing_email.title', 'Existing email'),
        message: t(
          'b.auth.create.email_taken.text',
          'This email is already taken, if it belongs to you please log in.'
        ),
        icon: EmailErrorIcon,
        button: {
          text: t('common.login', 'Log In'),
          onClick: () =>
            showLoginModal({
              eventId: 'web-signup-submit-landlord-emailtaken',
              t
            })
        },
        secondaryButton: {
          text: t('b.auth.create.try_again.button', 'Try Again')
        }
      })
    } else {
      toast(body?.message, {
        title: t(
          'b.auth.create.registration_failed.text',
          'Registration Failed'
        ),
        type: 'error'
      })
      return
    }
    setSubmitting(false)
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {() => children}
    </Formik>
  )
}

const AccountFormProvider: React.FC<Props> = ({ children, ...rest }) => (
  <AccountProvider>
    <AccountForm {...rest}>{children}</AccountForm>
  </AccountProvider>
)

export default AccountFormProvider
